<template>
  <div v-if="show && audit" class="compliance-banner" :class="audit.mode" data-test="compliance-banner">
    <span class="cb-mode" data-test="cb-mode">{{ modeLabel }}</span>
    <span class="cb-headline">
      <strong>{{ audit.tickets_changed }}</strong> ticket(s) flagged
      —
      <span class="mono">{{ audit.labels_added_total }}</span> labels {{ audit.mode === "apply" ? "added" : "would be added" }}<template v-if="audit.labels_removed_total > 0">,
      <span class="mono">{{ audit.labels_removed_total }}</span> {{ audit.mode === "apply" ? "removed" : "would be removed" }}</template>.
    </span>
    <span class="cb-rules">
      Rules:
      <code v-for="r in audit.rules_enabled" :key="r" class="rule-chip">{{ r }}</code>
    </span>
    <span v-if="audit.errors.length" class="cb-errors" :title="errorList">
      ⚠ {{ audit.errors.length }} error(s)
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { ComplianceAudit } from "@/api/types";

const props = defineProps<{ audit?: ComplianceAudit }>();

const audit = computed<ComplianceAudit | null>(() => props.audit ?? null);

// Hide entirely when off OR when nothing is flagged. Keeps the banner
// signal-only — a clean sprint shouldn't render this strip at all.
const show = computed(() => {
  if (!audit.value) return false;
  if (audit.value.mode === "off") return false;
  return audit.value.tickets_changed > 0 || audit.value.errors.length > 0;
});

const modeLabel = computed(() => {
  switch (audit.value?.mode) {
    case "apply":   return "Compliance · APPLY";
    case "dry_run": return "Compliance · DRY-RUN";
    default:        return "Compliance";
  }
});

const errorList = computed(() =>
  (audit.value?.errors ?? []).map((e) => `${e.key ?? e.stage}: ${e.http ?? e.exc}`).join("\n"),
);
</script>

<style scoped>
.compliance-banner {
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: var(--surface-card, #fff);
  border: 1px solid var(--surface-border, #e5e7eb);
  border-left-width: 4px;
  font-size: 13px;
}
.compliance-banner.dry_run { border-left-color: #2563eb; background: #eff6ff; }
.compliance-banner.apply { border-left-color: #b91c1c; background: #fef2f2; }

.cb-mode {
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--text-muted, #6b7280);
  padding: 2px 8px; border-radius: 999px; border: 1px solid currentColor;
}
.compliance-banner.dry_run .cb-mode { color: #1d4ed8; }
.compliance-banner.apply .cb-mode { color: #b91c1c; }

.cb-headline strong { color: var(--text-color, #111827); }
.cb-rules { color: var(--text-muted, #6b7280); font-size: 12px; }
.rule-chip {
  display: inline-block; margin-right: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  background: var(--surface-elev, #f3f4f6);
  padding: 1px 6px; border-radius: 4px;
}
.cb-errors { color: #b91c1c; font-weight: 600; cursor: help; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
</style>
